import { useMemo, useState } from 'react'
import { analyseResultToEquipes, analyseResultToStatGroups, NOT_AGGREGATABLE } from '../utils/analyseResultMapper'
import { T } from '../theme'

// ── AUCUNE COULEUR EN DUR DANS CE FICHIER ─────────────────────────────────
//
// La version precedente portait `HOME_C = '#c0392b'`, un rouge qui ne venait
// d'aucun token : il identifiait l'equipe du coach, qui voyait donc ses
// propres passes en rouge. Tout ce qui suit vient de `theme.js`, ou les
// contrastes sont releves. Une couleur nouvelle se declare la-bas, pas ici.
const INK = T.ink
const MUTED = T.muted
const RULE = T.rule
const MONO = T.mono
const DISP = T.display

// QUI — identifient une equipe, jamais un jugement.
//
// APLAT ou TEXTE, c'est la seule question a se poser · une couleur d'identite
// posee en aplat (barre, pastille, remplissage de terrain) n'a pas de contrainte
// de contraste de texte, une couleur ECRITE en a une. D'ou deux sorties par
// equipe, et pas une.
const US = T.repUs               // aplats seulement · 3.21:1 sur blanc
const US_INK = T.repUsInk        // le bleu ne tient pas sur blanc en texte
const THEM = T.repThem           // aplats seulement · 2.17:1 sur blanc
const THEM_INK = T.repThemInk    // l'ambre ne tient pas sur blanc en texte

// ACCENT D'INTERFACE — onglet actif, filet de titre. Ce n'est PAS une equipe.
// Meme valeur que `US` aujourd'hui, sortie distincte volontairement : un futur
// ajustement du bleu d'equipe ne doit pas deplacer les onglets, et lire
// `US` sur un soulignement d'onglet laissait croire que si.
const ACCENT = T.blue

// COMBIEN — disent un ecart, jamais qui a raison.
const UP = T.repUp
const UP_BG = T.repUpBg
const UP_INK = T.repUpInk
const DOWN = T.repDown
const DOWN_BG = T.repDownBg
const DOWN_INK = T.repDownInk

const HEAD_BG = T.repHeadBg
const HEAD_INK = T.repHeadInk
const HEAD_MUTED = T.repHeadMuted

// ── NOMS D'EQUIPE · POURQUOI PAS LE NOM DU CLUB ───────────────────────────
//
// « Nous » et « Adversaire », en dur, et surtout PAS le nom du club du coach.
//
// Rien n'ancre `equipe_1` a une equipe reelle. La convention du mapper dit
// « equipe_1 = nous », MatchDetail supposait « equipe_1 = domicile », et le
// prestataire ne documente qu'« equipes detectees dans la video ». Les trois ne
// peuvent pas etre vraies ensemble.
//
// La tentative d'ancrage a echoue sur donnees reelles : sur a90f3e5e le
// resultat est 5-0 pour le club, les cinq buts en seconde periode, et le
// pipeline en detecte bien cinq mais les repartit 3-2 dont un dans une mi-temps
// vide. Le compte juste, l'attribution fausse — donc aucun ancrage possible par
// les buts. Voir P0-BUTS-ATTRIBUTION-001.
const US_LABEL = 'Nous'
const THEM_LABEL = 'Adversaire'

/**
 * BaseDeLAnalyse — sur quelles couleurs l'analyse a ete faite.
 *
 * CINQ LIGNES QUI TRANSFORMENT UNE ERREUR SILENCIEUSE EN ERREUR VISIBLE.
 *
 * Le prestataire fait un ECHO · il renvoie les couleurs qu'on lui a envoyees,
 * sans rien verifier. Une couleur perimee produit donc un rapport entierement
 * inverse — les statistiques de l'adversaire presentees comme celles du coach,
 * et la convention de couleur qui repeint tout a l'envers — sans qu'aucune
 * alerte n'existe nulle part.
 *
 * En affichant ce sur quoi l'analyse s'est basee, le coach voit sa faute EN
 * OUVRANT LE RAPPORT, au lieu de decouvrir des chiffres incoherents sans
 * comprendre pourquoi. C'est la seule verification possible : elle est humaine.
 *
 * RIEN QUAND LE BLOC EST ABSENT. Tout l'historique a ete analyse avant les
 * couleurs. Inventer une phrase la reviendrait a affirmer une identification
 * qui n'a pas eu lieu — exactement le defaut qu'on corrige.
 */
function BaseDeLAnalyse({ equipes }) {
  if (!equipes) return null
  return (
    <div style={{
      fontFamily: MONO, fontSize: 10, color: MUTED, lineHeight: 1.6,
      padding: '9px 0 0', marginBottom: 16,
      borderTop: `1px solid ${RULE}`,
    }}>
      Analyse basée sur : ton équipe en {equipes.nous}, adversaire en {equipes.adversaire}.
    </div>
  )
}

const TABS = [
  { id: 'full', label: 'Match complet' },
  { id: 'mt1', label: '1re période' },
  { id: 'mt2', label: '2e période' },
]

// ── CONVENTION TIRET ET ZERO (PRODUCT-012) ────────────────────────────────
// `0` est une mesure : l'equipe n'a rien fait. `–` est une absence de mesure :
// on ne sait pas. Les confondre fait passer une ignorance pour un constat.
const TIRET = '–'
const estNombre = (v) => typeof v === 'number' && Number.isFinite(v)
const fmt = (v, unit) => (estNombre(v) ? `${arrondi(v)}${unit || ''}` : TIRET)

// L'arrondi est une decision D'AFFICHAGE. Le backend renvoie des valeurs
// pleines depuis les indicateurs derives : c'est ici qu'on les tronque, jamais
// dans les donnees. Voir le commentaire de `derived_indicators` cote backend.
function arrondi(v) {
  if (!estNombre(v)) return v
  if (Number.isInteger(v)) return v
  return Math.abs(v) >= 10 ? Math.round(v) : Math.round(v * 100) / 100
}

// ── CE QUI EST CALCULE ICI, ET CE QUI NE L'EST PAS ────────────────────────
//
// P1-VESTIGES-CLIENT-001 interdit de PRODUIRE une statistique cote client :
// pas de moyenne de pourcentages, pas d'agregat reconstitue. Un repli qui
// faisait exactement cela vient d'etre retire de MatchDetail (#222).
//
// Comparer deux valeurs deja fournies pour les mettre en forme n'est pas
// produire une statistique : l'ecart ci-dessous ne sort jamais du rendu, il
// n'est ni stocke, ni renvoye, ni agrege. C'est la difference entre calculer
// une donnee et decider comment l'afficher.
const ecartRelatif = (v1, v2) => {
  if (!estNombre(v1) || !estNombre(v2)) return null
  const base = (Math.abs(v1) + Math.abs(v2)) / 2
  if (base === 0) return null
  return ((v1 - v2) / base) * 100
}

function Pastille({ v1, v2, unit }) {
  if (!estNombre(v1) || !estNombre(v2)) return null
  const diff = v1 - v2
  if (diff === 0) {
    return (
      <span style={{
        fontFamily: MONO, fontSize: 10, fontWeight: 700, padding: '2px 6px',
        background: T.bgAlt, color: MUTED, whiteSpace: 'nowrap',
      }}>
        =
      </span>
    )
  }
  const favorable = diff > 0
  return (
    <span style={{
      fontFamily: MONO, fontSize: 10, fontWeight: 700, padding: '2px 6px',
      background: favorable ? UP_BG : DOWN_BG,
      color: favorable ? UP_INK : DOWN_INK,
      whiteSpace: 'nowrap',
    }}>
      {favorable ? '+' : '−'}{fmt(Math.abs(diff), unit)}
    </span>
  )
}

// Barre de comparaison fine. Les deux moities partagent la largeur au prorata,
// donc la barre se lit sans axe ni graduation.
//
// ELLE RECOIT LES VALEURS ARRONDIES, PAS LES BRUTES · voir `LigneStat`. Une
// barre qui contredit les deux chiffres imprimes juste au-dessus est pire
// qu'une barre absente.
function Barre({ v1, v2, epaisseur = 4 }) {
  if (!estNombre(v1) || !estNombre(v2)) return null
  const total = Math.abs(v1) + Math.abs(v2)
  const p1 = total === 0 ? 50 : (Math.abs(v1) / total) * 100
  // `data-barre` EST UN POINT D'ANCRAGE DE TEST, pas de la decoration.
  // `test/barres_rapport_check.jsx` relit le HTML rendu pour verifier
  // l'invariant BARRES-ARRONDI-001. Il ciblait la chaine de style exacte
  // (`height:3px`), ce qui liait un invariant metier a une valeur de
  // presentation · changer l'epaisseur cassait le test sans rien casser
  // dans le produit. Ne pas retirer cet attribut.
  return (
    <div data-barre style={{ display: 'flex', height: epaisseur, background: T.bgAlt, marginTop: 6 }}>
      <div style={{ width: `${p1}%`, background: US }} />
      <div style={{ width: `${100 - p1}%`, background: THEM }} />
    </div>
  )
}

// ── LE FAIT DU MATCH ───────────────────────────────────────────────────────
//
// Selection PAR REGLE, jamais ecrite a la main : la stat dont l'ecart relatif
// est le plus fort en valeur absolue. Sous le seuil, le bloc n'existe pas —
// mieux vaut ne rien dire que souligner un ecart insignifiant.
//
// La phrase reste FACTUELLE. « 44 ballons recuperes contre 31 » se verifie
// ligne a ligne dans le detail. « Ton equipe a domine » ne se verifie pas :
// c'est une lecture tactique, et P1-TACTIQUE-001 est en pause.
const SEUIL_FAIT_DU_MATCH = 25   // % d'ecart relatif

function faitDuMatch(groups) {
  if (!groups) return null
  const candidats = [
    ...(groups.transitions || []),
    ...(groups.passes || []),
    ...(groups.possession || []),
  ]
  let meilleur = null
  for (const [v1, v2, label, unit] of candidats) {
    const ecart = ecartRelatif(v1, v2)
    if (ecart === null) continue
    if (!meilleur || Math.abs(ecart) > Math.abs(meilleur.ecart)) {
      meilleur = { v1, v2, label, unit, ecart }
    }
  }
  if (!meilleur || Math.abs(meilleur.ecart) < SEUIL_FAIT_DU_MATCH) return null
  return meilleur
}

function FaitDuMatch({ groups }) {
  const fait = useMemo(() => faitDuMatch(groups), [groups])
  if (!fait) return null
  const favorable = fait.ecart > 0
  return (
    <div data-print="block" style={{
      background: T.surface, border: `1px solid ${RULE}`,
      borderLeft: `3px solid ${favorable ? UP : DOWN}`,
      padding: '20px 22px', marginBottom: 24,
      display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
    }}>
      <span style={{
        fontFamily: DISP, fontSize: 44, fontWeight: 800, lineHeight: 1,
        color: favorable ? UP : DOWN,
      }}>
        {fmt(fait.v1, fait.unit)}
      </span>
      <span style={{ fontFamily: MONO, fontSize: 13, color: INK, lineHeight: 1.5 }}>
        {fait.label} · <strong>{fmt(fait.v1, fait.unit)}</strong> pour {US_LABEL.toLowerCase()},{' '}
        <strong>{fmt(fait.v2, fait.unit)}</strong> pour l'{THEM_LABEL.toLowerCase()}.
      </span>
    </div>
  )
}

// ── QUATRE CHIFFRES CLES ───────────────────────────────────────────────────
// Choisis dans un ordre de preference, et seulement s'ils sont mesures des DEUX
// cotes. Sur le match complet la possession est absente (P0-STATS-001) : la
// liste se decale d'elle-meme vers les compteurs bruts, sans trou.
//
// LES QUATRE PREMIERS SONT CEUX DU BANDEAU · passes reussies, precision,
// ballons recuperes, ballons surface adverse. La possession est descendue en
// cinquieme place : elle a desormais sa barre epaisse en tete du detail, la
// reprendre en carte la disait deux fois.
const PREFERENCES_CLES = [
  'Passes réussies',
  'Précision de passe',
  'Ballons récupérés',
  'Ballons surface adverse',
  'Possession globale',
  'Récupérations < 5 s',
]

function ChiffresCles({ groups }) {
  const cles = useMemo(() => {
    if (!groups) return []
    const toutes = [
      ...(groups.possession || []),
      ...(groups.passes || []),
      ...(groups.transitions || []),
    ]
    const trouvees = []
    for (const label of PREFERENCES_CLES) {
      const ligne = toutes.find((l) => l[2] === label && estNombre(l[0]) && estNombre(l[1]))
      if (ligne) trouvees.push(ligne)
      if (trouvees.length === 4) break
    }
    return trouvees
  }, [groups])

  if (!cles.length) return null
  // GRILLE AUTO-AJUSTABLE PLUTOT QUE `repeat(n, 1fr)` · a quatre colonnes sur
  // un telephone, chaque carte tombait sous 70 px et son libelle se coupait en
  // quatre lignes. `minmax(140px, 1fr)` replie en deux colonnes puis une, sans
  // point de rupture a maintenir.
  return (
    <div data-print="block" style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      border: `1px solid ${RULE}`, background: T.surface, marginBottom: 24,
    }}>
      {cles.map(([v1, v2, label, unit]) => (
        <div key={label} style={{
          padding: '16px 14px',
          borderLeft: `1px solid ${RULE}`, borderTop: `1px solid ${RULE}`,
          margin: '-1px 0 0 -1px',
        }}>
          <div style={{
            fontFamily: MONO, fontSize: 9, letterSpacing: '.1em',
            textTransform: 'uppercase', color: MUTED, marginBottom: 8,
            minHeight: 22, lineHeight: 1.3,
          }}>
            {label}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: DISP, fontSize: 32, fontWeight: 700, lineHeight: 1, color: INK }}>
              {fmt(v1, unit)}
            </span>
            {/* L'ECART PORTE UN SIGNE, PAS SEULEMENT UNE COULEUR. `data-print`
                force le noir a l'impression (theme.js) · un ecart qui ne se
                lit que par le vert ou le rouge devient muet sur papier, et ce
                rapport EST imprime — report_pdf.py rend cet onglet meme. Le
                `+` et le `−` de Pastille survivent a la mise en noir. */}
            <Pastille v1={v1} v2={v2} unit={unit} />
          </div>
          {/* La valeur adverse est ecrite, pas seulement suggeree par l'ecart ·
              sans elle le coach doit soustraire de tete pour la retrouver. */}
          <div style={{ fontFamily: MONO, fontSize: 10, color: THEM_INK, marginTop: 6 }}>
            {THEM_LABEL} {fmt(v2, unit)}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── DETAIL ─────────────────────────────────────────────────────────────────
// Rend « 45/52 » ou le tiret · `parseRatio` remplit `ok` et `total` a null
// quand la chaine du prestataire est absente ou illisible.
function ratioTexte(r) {
  if (!r || !estNombre(r.ok) || !estNombre(r.total)) return TIRET
  return `${r.ok}/${r.total}`
}

function LigneStat({ v1, v2, label, unit, ratio1 = null, ratio2 = null, unavailable = false }) {
  // METRIQUE NON QUALIFIEE · on garde la ligne et son libelle, on retire la
  // valeur. Le bloc doit rester visible : le coach qui cherche ses corners a
  // besoin de voir que la question est connue et pas encore repondue, plutot
  // que de ne rien trouver et de supposer un bug.
  //
  // NI TIRET NI ZERO · le tiret dit « pas mesure sur ce match » et le zero dit
  // « mesure a zero ». Ici c'est la METRIQUE qui n'est pas fiable, sur tous les
  // matchs — un etat que le vocabulaire existant ne sait pas dire.
  //
  // GRIS, PLUS PETIT, SANS BARRE · les trois signaux disent la meme chose ·
  // ce n'est pas une donnee. La couleur d'equipe (US_INK / THEM_INK) et la
  // barre de proportion appartiennent aux valeurs mesurees ; les poser ici
  // ferait passer « À venir » pour un resultat.
  if (unavailable) {
    return (
      <div style={{ padding: '10px 0', borderBottom: `1px solid ${RULE}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: '58px 1fr 58px', gap: 10, alignItems: 'baseline' }}>
          <span data-v="1" style={{ fontFamily: DISP, fontSize: 12, fontWeight: 700, color: MUTED, textAlign: 'left' }}>
            À venir
          </span>
          <span style={{ fontFamily: MONO, fontSize: 11, color: INK, textAlign: 'center', lineHeight: 1.4 }}>
            {label}
          </span>
          <span data-v="2" style={{ fontFamily: DISP, fontSize: 12, fontWeight: 700, color: MUTED, textAlign: 'right' }}>
            À venir
          </span>
        </div>
      </div>
    )
  }
  // Non agregeable sur le match complet : on le dit plutot que d'afficher un
  // zero qui passerait pour une mesure. Sur cette vue, la comparaison des deux
  // periodes prend le relais juste en dessous.
  if (v1 === NOT_AGGREGATABLE || v2 === NOT_AGGREGATABLE) {
    return (
      <div style={{ padding: '10px 0', borderBottom: `1px solid ${RULE}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 16, alignItems: 'baseline' }}>
          <span style={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>{label}</span>
          <span style={{ fontFamily: MONO, fontSize: 11, color: T.muted2, gridColumn: 'span 2' }}>
            Voir par période
          </span>
        </div>
      </div>
    )
  }
  // ── LECTURE CENTREE · valeur nous · libelle · valeur adversaire ───────
  //
  // Le libelle etait a gauche et les deux valeurs collees a droite : pour
  // comparer, l'oeil devait parcourir toute la ligne puis revenir. Le libelle
  // au centre met les deux chiffres aux extremites, de part et d'autre de la
  // barre qui les partage · la comparaison se fait sans deplacement.
  //
  // COLONNES FIXES AUX EXTREMITES (58 px), LIBELLE ELASTIQUE AU CENTRE ·
  // l'inverse laissait les chiffres bouger d'une ligne a l'autre selon la
  // longueur du texte, et la colonne cessait de se lire de haut en bas.
  //
  // TOUJOURS PAS DE LARGEUR MINIMALE SUR LA GRILLE. La version d'avant portait
  // `minWidth: 360`, qui imposait un scroll horizontal PERMANENT sur mobile :
  // la largeur utile y vaut ~288 px (padding DashboardLayout + Card, des deux
  // cotes), soit 72 px de moins que ce qu'elle exigeait. Le livrable central du
  // produit etait a faire glisser du doigt a chaque consultation. 58 + 58 + les
  // deux gouttieres tiennent largement dans les 288 px, le libelle absorbe le
  // reste. Ne pas reintroduire de minWidth.
  return (
    <div style={{ padding: '10px 0', borderBottom: `1px solid ${RULE}` }}>
      <div style={{ display: 'grid', gridTemplateColumns: '58px 1fr 58px', gap: 10, alignItems: 'baseline' }}>
        <span data-v="1" style={{ fontFamily: DISP, fontSize: 19, fontWeight: 700, color: US_INK, textAlign: 'left' }}>
          {fmt(v1, unit)}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 11, color: INK, textAlign: 'center', lineHeight: 1.4 }}>
          {label}
          {/* LE RATIO BRUT, quand le prestataire l'envoie · « 45/52 » sous
              « 87 % ». Le pourcentage seul ne dit pas sur combien de passes il
              est calcule, et 92 % sur 12 passes n'est pas 92 % sur 240. */}
          {(ratio1 || ratio2) && (
            <span style={{ display: 'block', fontSize: 9, color: MUTED, marginTop: 2 }}>
              {ratioTexte(ratio1)} · {ratioTexte(ratio2)}
            </span>
          )}
        </span>
        <span data-v="2" style={{ fontFamily: DISP, fontSize: 19, fontWeight: 700, color: THEM_INK, textAlign: 'right' }}>
          {fmt(v2, unit)}
        </span>
      </div>
      {/* LA BARRE LIT LES MEMES VALEURS QUE LES CHIFFRES · arrondies.
          Elle lisait les valeurs BRUTES, ce qui la mettait en contradiction
          avec les deux nombres imprimes juste au-dessus des qu'un arrondi les
          rapprochait. Releve sur le match ba15d1af · « Passes vers l'arriere »
          affichait 19 % / 19 % (brut 19,1 et 18,5) au-dessus d'une barre
          partagee a 50,8 / 49,2. L'ecart est invisible dans les chiffres et
          visible dans la barre : le coach lit une difference que le rapport
          lui dit par ailleurs inexistante.
          `arrondi` est idempotent, les libelles ne bougent donc pas d'un
          pixel · seule la geometrie de la barre change. */}
      <Barre v1={arrondi(v1)} v2={arrondi(v2)} epaisseur={5} />
    </div>
  )
}

function Bloc({ titre, lignes }) {
  if (!lignes?.length) return null
  return (
    <div data-print="block" style={{ marginBottom: 28 }}>
      <div style={{
        fontFamily: DISP, fontSize: 15, fontWeight: 700, letterSpacing: '.08em',
        textTransform: 'uppercase', color: INK, marginBottom: 4,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ width: 14, height: 1, background: ACCENT, display: 'inline-block' }} />
        {titre}
      </div>
      {/* L'EN-TETE SUIT EXACTEMENT LA GRILLE DES LIGNES · sinon « Nous » ne
          surplombe plus la colonne qu'il nomme, et l'ecart se voit surtout au
          moment ou on ne le cherche pas. */}
      <div style={{
        display: 'grid', gridTemplateColumns: '58px 1fr 58px', gap: 10,
        padding: '6px 0', borderBottom: `1px solid ${T.ruleDark}`,
      }}>
        <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: US_INK, textAlign: 'left', textTransform: 'uppercase' }}>
          {US_LABEL}
        </span>
        <span />
        <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: THEM_INK, textAlign: 'right', textTransform: 'uppercase' }}>
          {THEM_LABEL}
        </span>
      </div>
      {/* LE 5e ELEMENT DU TUPLE PORTE DEUX CHOSES DIFFERENTES · un ratio brut
          (`passesZones`, avec `ok` / `total`) ou les options de la ligne
          (`tirs`, avec `unavailable`). On les distingue sur la presence de la
          clef plutot que sur la position, sinon les trois lignes de
          « Passes par zone » liraient les options comme un ratio et
          afficheraient « – · – » sous chaque libelle. Un tuple a quatre
          elements passe ici sans rien porter, comme avant. */}
      {lignes.map(([v1, v2, label, unit, opt1, opt2]) => {
        const options = opt1 && typeof opt1 === 'object' && 'unavailable' in opt1 ? opt1 : null
        return (
          <LigneStat
            key={label}
            v1={v1}
            v2={v2}
            label={label}
            unit={unit}
            ratio1={options ? null : opt1}
            ratio2={options ? null : opt2}
            unavailable={Boolean(options?.unavailable)}
          />
        )
      })}
    </div>
  )
}

// ── COMPARAISON DES DEUX PERIODES ──────────────────────────────────────────
//
// LE BLOC QUI JUSTIFIE CETTE REFONTE.
//
// Trois familles ne peuvent pas etre agregees sur le match complet : le
// prestataire ne renvoie que des pourcentages, sans les compteurs bruts qui
// permettraient de les recombiner (P0-STATS-001). Le rapport affichait donc
// « Voir par période » sur ces lignes, et ne montrait RIEN.
//
// On montre les deux periodes cote a cote. Aucune estimation, aucune moyenne :
// ce sont les valeurs telles que le prestataire les a rendues, simplement
// mises en regard. Un coach y lit ce qu'un chiffre moyen lui aurait cache —
// avoir subi puis domine, ou l'inverse.
const FAMILLES_PAR_PERIODE = [
  { cle: 'possession', titre: 'Possession' },
  { cle: 'attaques', titre: 'Origine des attaques' },
]

function ComparaisonPeriodes({ groupsMt1, groupsMt2 }) {
  if (!groupsMt1 || !groupsMt2) return null
  return (
    // `data-rapport="match-complet"` · CONSOMME PAR LE BACKEND · l'export PDF
    // (app/utils/report_pdf.py) attend cet attribut pour savoir que la vue
    // rendue est bien le match complet, et non un onglet de periode. Ne pas le
    // retirer sans corriger le selecteur la-bas · sans lui, un PDF partirait
    // avec la 2e periode, ce qui est deja arrive le 05/08.
    <div data-rapport="match-complet" data-print="block" style={{ marginBottom: 28 }}>
      <div style={{
        fontFamily: DISP, fontSize: 15, fontWeight: 700, letterSpacing: '.08em',
        textTransform: 'uppercase', color: INK, marginBottom: 4,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ width: 14, height: 1, background: ACCENT, display: 'inline-block' }} />
        Par période
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 48px 48px 48px 48px', gap: 8,
        padding: '6px 0', borderBottom: `1px solid ${T.ruleDark}`,
      }}>
        <span />
        <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: MUTED, textAlign: 'center', gridColumn: 'span 2' }}>
          1re période
        </span>
        <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: MUTED, textAlign: 'center', gridColumn: 'span 2' }}>
          2e période
        </span>
      </div>

      {FAMILLES_PAR_PERIODE.map(({ cle, titre }) => {
        const l1 = groupsMt1[cle] || []
        const l2 = groupsMt2[cle] || []
        if (!l1.length) return null
        return (
          <div key={cle} style={{ marginTop: 14 }}>
            <div style={{
              fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '.08em',
              textTransform: 'uppercase', color: MUTED, marginBottom: 4,
            }}>
              {titre}
            </div>
            {l1.map(([a1, a2, label, unit], i) => {
              const [b1, b2] = l2[i] || [null, null]
              return (
                <div key={label} style={{
                  display: 'grid', gridTemplateColumns: '1fr 48px 48px 48px 48px', gap: 8,
                  padding: '8px 0', borderBottom: `1px solid ${RULE}`, alignItems: 'baseline',
                }}>
                  <span style={{ fontFamily: MONO, fontSize: 12, color: INK }}>{label}</span>
                  <span style={{ fontFamily: DISP, fontSize: 18, fontWeight: 700, color: US_INK, textAlign: 'right' }}>{fmt(a1, unit)}</span>
                  <span style={{ fontFamily: DISP, fontSize: 18, fontWeight: 700, color: THEM_INK, textAlign: 'right' }}>{fmt(a2, unit)}</span>
                  <span style={{ fontFamily: DISP, fontSize: 18, fontWeight: 700, color: US_INK, textAlign: 'right' }}>{fmt(b1, unit)}</span>
                  <span style={{ fontFamily: DISP, fontSize: 18, fontWeight: 700, color: THEM_INK, textAlign: 'right' }}>{fmt(b2, unit)}</span>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

// ── TERRAIN EN TROIS TIERS ─────────────────────────────────────────────────
//
// ⚠ LE PIÈGE DE CE COMPOSANT · LES ZONES SONT RELATIVES À CHAQUE ÉQUIPE.
//
// `zones_pct.defensive.equipe_1` et `zones_pct.defensive.equipe_2` ne désignent
// PAS la même portion de pelouse. Ce sont les deux moitiés OPPOSÉES du terrain :
// le tiers défensif du club est spatialement le tiers offensif de l'adversaire.
//
// Le terrain est dessiné du point de vue du club, sens d'attaque vers la
// DROITE. Les valeurs adverses doivent donc être RETOURNÉES ·
//
//   tiers affiché      valeur nous          valeur adversaire
//   défensif (gauche)  defensive.equipe_1   offensive.equipe_2
//   médian  (centre)   milieu.equipe_1      milieu.equipe_2
//   offensif (droite)  offensive.equipe_1   defensive.equipe_2
//
// Superposer les deux séries sans retourner produit un affichage FAUX qui a
// l'air précis. C'est le défaut le plus coûteux possible ici : personne ne peut
// le voir à l'œil sur un rendu, seule une assertion le rattrape.
// Couvert par `test/render_check.jsx`, cas « terrain · zones adverses retournees ».

const TIERS = [
  { cle: 'defensive', libelle: 'Défensif' },
  { cle: 'milieu',    libelle: 'Médian' },
  { cle: 'offensive', libelle: 'Offensif' },
]

// Le retournement, isolé et pur pour être testable seul.
export function zonesOrienteesClub(zonesPct) {
  if (!zonesPct || typeof zonesPct !== 'object') return null
  const lire = (cle, equipe) => {
    const v = zonesPct[cle]?.[`equipe_${equipe}`]
    return estNombre(v) ? v : null
  }
  const nous = TIERS.map(t => lire(t.cle, 1))
  // L'ordre est INVERSÉ pour l'adversaire : son offensif est notre défensif.
  const adverse = [...TIERS].reverse().map(t => lire(t.cle, 2))
  if (nous.every(v => v === null) && adverse.every(v => v === null)) return null
  return { nous, adverse }
}

// L'intensité passe par l'opacité, jamais par une couleur fabriquée.
// Plafonnée à .24 : au-delà, le texte posé dessus perd son contraste (theme.js).
const OPACITE_MAX = 0.24
const opaciteTiers = (v) => (estNombre(v) ? (Math.max(0, Math.min(100, v)) / 100) * OPACITE_MAX : 0)

function Terrain({ titre, zonesPct }) {
  const z = zonesOrienteesClub(zonesPct)
  // Pas de zones mesurées → pas de terrain. Ni zéro, ni terrain vide.
  if (!z) return null

  // RATIO REEL D'UN TERRAIN · 105 x 68 m, soit 1,544. Le viewBox le reprend a
  // l'echelle 3 px/m.
  const W = 315, H = 204, T3 = W / 3
  // BASE FLEX 300 px · deux terrains plus le gap font 620, donc l'empilement se
  // declenche des 620 px de large. Sous ce seuil un terrain tomberait sous
  // 290 px et ses chiffres deviendraient illisibles — on empile plutot que de
  // retrecir.
  //
  // PLAFOND DE LARGEUR · sans lui, un terrain SEUL (onglet de periode, ou match
  // sans seconde mi-temps) s'etire sur toute la colonne et occupe une demi-page
  // pour six chiffres. Le ratio fait le reste · 380 / 1,544 = 246 px de haut,
  // donc le bloc reste sous le tiers d'un ecran de bureau.
  return (
    <div data-print="block" style={{ flex: '1 1 300px', minWidth: 0, maxWidth: 380 }}>
      <div style={{
        fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '.12em',
        textTransform: 'uppercase', color: MUTED, marginBottom: 8, textAlign: 'center',
      }}>
        {titre}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img"
           aria-label={`Possession par tiers, ${titre}`}
           style={{ display: 'block', background: T.repPitchBg }}>
        {/* Remplissage · l'intensité du bleu dit la possession du club */}
        {z.nous.map((v, i) => (
          <rect key={`f${i}`} x={i * T3} y={0} width={T3} height={H}
                fill={US} fillOpacity={opaciteTiers(v)} />
        ))}

        {/* Marquages · ils situent, ils ne portent pas l'information */}
        <rect x={0.5} y={0.5} width={W - 1} height={H - 1} fill="none" stroke={T.repPitchLine} />
        <line x1={T3} y1={0} x2={T3} y2={H} stroke={T.repPitchLine} strokeDasharray="3 4" />
        <line x1={2 * T3} y1={0} x2={2 * T3} y2={H} stroke={T.repPitchLine} strokeDasharray="3 4" />
        <line x1={W / 2} y1={0} x2={W / 2} y2={H} stroke={T.repPitchLine} />
        <circle cx={W / 2} cy={H / 2} r={26} fill="none" stroke={T.repPitchLine} />
        <rect x={0.5} y={H / 2 - 38} width={38} height={76} fill="none" stroke={T.repPitchLine} />
        <rect x={W - 38.5} y={H / 2 - 38} width={38} height={76} fill="none" stroke={T.repPitchLine} />

        {/* Valeurs · la nôtre en grand, celle de l'adversaire en dessous */}
        {TIERS.map((t, i) => {
          const cx = i * T3 + T3 / 2
          return (
            <g key={t.cle}>
              {/* TAILLES DANS L'ECHELLE DU VIEWBOX, pas en pixels d'ecran · a
                  380 px de rendu pour 315 d'echelle, le facteur vaut ~1,2.
                  Ces chiffres restent nettement sous le score du match en tete
                  de rapport (46 a 56 px) · le terrain est un support de
                  lecture, pas un cadre autour de chiffres geants. */}
              <text x={cx} y={H / 2 - 10} textAnchor="middle"
                    style={{ fontFamily: DISP, fontSize: 17, fontWeight: 700, fill: INK }}>
                {fmt(z.nous[i], '%')}
              </text>
              <text x={cx} y={H / 2 + 8} textAnchor="middle"
                    style={{ fontFamily: DISP, fontSize: 12, fontWeight: 700, fill: T.repPitchThem }}>
                {fmt(z.adverse[i], '%')}
              </text>
              <text x={cx} y={H - 10} textAnchor="middle"
                    style={{ fontFamily: MONO, fontSize: 7, letterSpacing: '.1em', fill: MUTED }}>
                {t.libelle.toUpperCase()}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

function BlocTerrain({ periodes }) {
  const rendus = periodes.filter(p => zonesOrienteesClub(p.zonesPct))
  if (!rendus.length) return null
  return (
    <div data-print="block" style={{ marginBottom: 28 }}>
      <div style={{
        fontFamily: DISP, fontSize: 15, fontWeight: 700, letterSpacing: '.08em',
        textTransform: 'uppercase', color: INK, marginBottom: 4,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ width: 14, height: 1, background: ACCENT, display: 'inline-block' }} />
        Possession sur le terrain
      </div>
      <p style={{ fontFamily: MONO, fontSize: 11, color: MUTED, margin: '0 0 14px', lineHeight: 1.5 }}>
        Part du temps de possession passée dans chaque tiers.
      </p>
      {/* Sous 600 px les terrains s'empilent plutot que de retrecir : un
          terrain de 130 px de large ne se lit plus. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
        {rendus.map(p => <Terrain key={p.titre} titre={p.titre} zonesPct={p.zonesPct} />)}
      </div>
      {/* UNE LEGENDE DIT COMMENT LIRE CE QUI EST AFFICHE.
          Elle ne documente pas l'implementation et elle n'annonce pas ce que le
          produit ne sait pas faire. Cette legende portait les deux : le
          retournement des tiers adverses — une contrainte technique, qui reste
          dans le code et dans les tests — et l'absence des couloirs. Un coach
          qui paie 39 € n'a pas a lire la liste de ce qu'on ne lui donne pas. */}
      {/* PDF-RAPPORT-002 · « Terrain vu de notre banc, on attaque vers la
          droite. » est retiree. Elle affirmait un sens d'attaque que RIEN ne
          mesure. Verite terrain relevee par le coach sur le match d826fe24 :
          le club attaquait vers la GAUCHE en 1re periode et vers la DROITE en
          2e — la legende annoncait une seule direction pour les deux, et se
          trompait donc au moins une fois sur deux. Le dossier va plus loin
          (`insightball-context/00_ETAT_PROJET.md`, « L'orientation des couloirs
          reste indeterminable ») · gauche et droite se repondent
          symetriquement dans les deux lectures, donc AUCUN volume de donnees
          ne tranchera — il faut une verite terrain exterieure. Le rapport
          affirmait ce que le dossier declare non seulement inconnu, mais
          inconnaissable en l'etat.

          PAS REMPLACEE PAR UNE AUTRE DIRECTION, ni conditionnee a domicile /
          exterieur · l'information n'existe pas. Un champ qu'on ne sait pas
          calculer est absent, jamais approxime. Ne remettre une phrase de sens
          d'attaque que le jour ou une mesure la produit. */}
      <p style={{ fontFamily: MONO, fontSize: 10, color: MUTED, margin: '12px 0 0', lineHeight: 1.6 }}>
        <strong style={{ color: INK }}>{US_LABEL}</strong> en grand,{' '}
        <strong style={{ color: T.repPitchThem }}>{THEM_LABEL.toLowerCase()}</strong> en dessous.
      </p>
    </div>
  )
}

// ── INDICATEURS DERIVES ────────────────────────────────────────────────────
//
// Calcules par le BACKEND (analysis_api.derived_indicators). Le composant lit
// la valeur et la legende telles qu'elles arrivent : la formule voyage avec le
// chiffre, elle n'est jamais reecrite ici. Si le backend change une formule,
// la legende suit sans qu'on touche au frontend.
// UN INDICATEUR MANQUANT DECRIT L'ETAT DE LA DONNEE, PAS NOTRE CAPACITE.
// « non calculable » disait notre echec ; le coach veut savoir ce qui s'est
// passe sur son match. Le backend distingue deja les deux causes, on s'en sert ·
//   `donnee_absente`    · le compteur n'a pas ete fourni — on ne sait pas
//   `denominateur_nul`  · il a ete fourni a zero — l'action ne s'est pas produite
// Deux etats differents, deux phrases differentes, aucun jargon (PRODUCT-012).
function texteOmission(omissions) {
  const causes = Object.values(omissions || {})
  if (!causes.length) return ''
  const toutesNulles = causes.every(c => c === 'denominateur_nul')
  return toutesNulles
    ? ' · aucune action de ce type sur ce match'
    : ' · donnée indisponible sur ce match'
}

function Indicateurs({ source }) {
  const bloc = source?.indicateurs_derives
  if (!bloc || typeof bloc !== 'object') return null
  const entrees = Object.entries(bloc).filter(([, v]) => v && typeof v === 'object')
  if (!entrees.length) return null

  return (
    <div data-print="block" style={{ marginBottom: 28 }}>
      <div style={{
        fontFamily: DISP, fontSize: 15, fontWeight: 700, letterSpacing: '.08em',
        textTransform: 'uppercase', color: INK, marginBottom: 12,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ width: 14, height: 1, background: ACCENT, display: 'inline-block' }} />
        Indicateurs
      </div>
      {/* EN CARTES, PAS EN LIGNES · un indicateur derive porte sa formule,
          souvent plus longue que son libelle. En ligne, cette formule passait
          sous les deux valeurs et cassait l'alignement de toute la colonne.
          La carte lui donne sa place sans deranger ses voisines.
          `auto-fit` replie en une colonne sur telephone. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        {entrees.map(([cle, v]) => {
          const methode = v.methode || {}
          const omissions = methode.omissions || {}
          return (
            <div key={cle} data-print="block" style={{
              background: T.surface, border: `1px solid ${RULE}`,
              borderTop: `2px solid ${ACCENT}`, padding: '14px 16px',
            }}>
              <div style={{
                fontFamily: MONO, fontSize: 9, letterSpacing: '.1em',
                textTransform: 'uppercase', color: MUTED, marginBottom: 10, lineHeight: 1.3,
              }}>
                {methode.libelle || cle}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ fontFamily: DISP, fontSize: 28, fontWeight: 700, color: US_INK }}>
                  {fmt(v.equipe_1)}
                </span>
                <span style={{ fontFamily: DISP, fontSize: 20, fontWeight: 700, color: THEM_INK }}>
                  {fmt(v.equipe_2)}
                </span>
              </div>
              {methode.formule && (
                <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginTop: 8, lineHeight: 1.5 }}>
                  {methode.formule}
                  {texteOmission(omissions)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── BARRE DE POSSESSION ────────────────────────────────────────────────────
//
// La seule barre epaisse du rapport, et elle ouvre son bloc. La possession est
// la statistique que le coach cherche en premier · lui donner une forme
// distincte evite de la faire chercher parmi quinze lignes identiques.
//
// Elle lit la ligne « Possession globale » du groupe plutot qu'un champ a
// part · une seule source pour une seule notion. Sur le match complet cette
// ligne vaut NOT_AGGREGATABLE (P0-STATS-001) · la barre ne se rend pas, et la
// comparaison par periode juste en dessous prend le relais.
function BarrePossession({ lignes }) {
  const ligne = (lignes || []).find((l) => l[2] === 'Possession globale')
  if (!ligne) return null
  const [brut1, brut2] = ligne
  const v1 = arrondi(brut1)
  const v2 = arrondi(brut2)
  if (!estNombre(v1) || !estNombre(v2)) return null
  const total = Math.abs(v1) + Math.abs(v2)
  const p1 = total === 0 ? 50 : (Math.abs(v1) / total) * 100
  return (
    <div data-print="block" style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontFamily: DISP, fontSize: 26, fontWeight: 800, color: US_INK }}>{fmt(v1, '%')}</span>
        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED }}>
          Possession
        </span>
        <span style={{ fontFamily: DISP, fontSize: 26, fontWeight: 800, color: THEM_INK }}>{fmt(v2, '%')}</span>
      </div>
      <Barre v1={v1} v2={v2} epaisseur={14} />
    </div>
  )
}

// ── TRANSITIONS PAR ZONE ───────────────────────────────────────────────────
//
// `zonesTurnover` est produit par le mapper DEPUIS LE DEBUT et n'etait rendu
// NULLE PART · trois zones fois quatre compteurs, douze valeurs que le
// prestataire envoie et que le coach n'a jamais vues.
//
// Rendu en deux familles plutot qu'en tableau a quatre colonnes · « perdus » et
// « recuperes » ne se comparent pas entre eux, chacun se compare a l'adversaire
// dans la meme zone. Le tableau invitait a lire en ligne, ce qui n'a pas de
// sens ici.
function BlocZonesTurnover({ lignes }) {
  if (!lignes?.length) return null
  const recuperes = lignes.map(([nom, r1, , r2]) => [r1, r2, nom, ''])
  const perdus = lignes.map(([nom, , p1, , p2]) => [p1, p2, nom, ''])
  const vide = (l) => l.every(([a, b]) => !estNombre(a) && !estNombre(b))
  if (vide(recuperes) && vide(perdus)) return null
  return (
    <>
      {!vide(recuperes) && <Bloc titre="Ballons récupérés par zone" lignes={recuperes} />}
      {!vide(perdus) && <Bloc titre="Ballons perdus par zone" lignes={perdus} />}
    </>
  )
}

// ── BANDEAU ────────────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

function Bandeau({ category, competition, matchDate, scoreUs, scoreThem }) {
  const dateTexte = formatDate(matchDate)
  const gauche = [category, competition, dateTexte].filter(Boolean)
  // LE SCORE EST CELUI SAISI PAR LE COACH, jamais celui deduit des buts
  // detectes (STATS-002). Le pipeline se trompe d'attribution, pas le coach.
  const hasScore = estNombre(scoreUs) && estNombre(scoreThem)
  return (
    <div data-print="light" style={{
      background: HEAD_BG, padding: '22px 24px', marginBottom: 24,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: 20, flexWrap: 'wrap',
    }}>
      <div>
        <div style={{
          fontFamily: DISP, fontSize: 20, fontWeight: 700, letterSpacing: '.04em',
          textTransform: 'uppercase', color: HEAD_INK,
        }}>
          Rapport de match
        </div>
        {gauche.length > 0 && (
          <div style={{ fontFamily: MONO, fontSize: 11, color: HEAD_MUTED, marginTop: 4 }}>
            {gauche.join(' · ')}
          </div>
        )}
      </div>
      {hasScore && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontFamily: DISP, fontSize: 46, fontWeight: 800, lineHeight: 1, color: US }}>
            {scoreUs}
          </span>
          <span style={{ fontFamily: DISP, fontSize: 28, color: HEAD_MUTED, lineHeight: 1 }}>–</span>
          <span style={{ fontFamily: DISP, fontSize: 46, fontWeight: 800, lineHeight: 1, color: HEAD_INK }}>
            {scoreThem}
          </span>
        </div>
      )}
    </div>
  )
}

function Legende() {
  return (
    <div data-print="block" style={{
      borderTop: `1px solid ${RULE}`, paddingTop: 14, marginTop: 8,
      fontFamily: MONO, fontSize: 10, color: MUTED, lineHeight: 1.7,
    }}>
      <div>
        <strong style={{ color: US_INK }}>{US_LABEL}</strong> et{' '}
        <strong style={{ color: THEM_INK }}>{THEM_LABEL}</strong> désignent les deux équipes
        détectées dans la vidéo.
      </div>
      <div>
        <span style={{ background: UP_BG, color: UP_INK, padding: '1px 5px', fontWeight: 700 }}>vert</span>
        {' '}et{' '}
        <span style={{ background: DOWN_BG, color: DOWN_INK, padding: '1px 5px', fontWeight: 700 }}>rouge</span>
        {' '}indiquent un écart au-dessus ou en dessous de l'adversaire. Ils ne portent aucun jugement
        et ne désignent jamais une équipe.
      </div>
      <div>
        <strong>{TIRET}</strong> signifie « non mesuré », <strong>0</strong> signifie « mesuré à zéro ».
        Ce sont deux états différents.
      </div>
      <div>
        Les indicateurs portent leur formule.
      </div>
    </div>
  )
}

export default function MatchAnalyseReport({
  mt1,
  mt2,
  full,
  category = null,
  competition = null,
  matchDate = null,
  scoreUs = null,
  scoreThem = null,
  loading = false,
  error = null,
  periodeInitiale = null,
}) {
  // ── L'ONGLET EST DERIVE A CHAQUE RENDU, JAMAIS FIGE ──────────────────────
  //
  // UN INITIALISEUR DE `useState` NE SE REEVALUE JAMAIS. La version precedente
  // faisait `useState(() => full ? 'full' : mt1 ? 'mt1' : 'mt2')` · au montage,
  // les trois etaient `null` — l'onglet Rapport de MatchDetail existe PENDANT
  // le chargement (`show: hasIaReport || analyseResultsLoading`), donc le
  // composant se montait avant l'arrivee des donnees. L'initialiseur tombait
  // sur la derniere branche, `mt2`, et n'en bougeait plus.
  //
  // Consequence observee · un lien `?tab=rapport` ouvrait la 2e periode au lieu
  // du match complet, a l'ecran comme dans le PDF. Le rendu etait present et
  // FAUX — le pire des deux.
  //
  // Ici l'etat ne garde que le choix EXPLICITE de l'utilisateur ; l'onglet
  // rendu est recalcule a chaque rendu selon ce qui est disponible. Meme motif
  // que `ongletActif` dans MatchDetail.
  const [tabChoisi, setTabChoisi] = useState(null)

  // `full` SEUL, jamais deduit de la presence des deux mi-temps. L'agregat du
  // match complet vient du backend ; s'il manque, l'onglet n'est pas propose.
  const available = useMemo(() => ({
    full: Boolean(full),
    mt1: Boolean(mt1),
    mt2: Boolean(mt2),
  }), [mt1, mt2, full])

  // Ordre de preference · le choix de l'utilisateur, puis la periode demandee
  // par l'URL, puis le match complet. On ne retombe sur une mi-temps que si le
  // match complet n'existe pas.
  const tab = useMemo(() => {
    for (const candidat of [tabChoisi, periodeInitiale]) {
      if (candidat && available[candidat]) return candidat
    }
    return available.full ? 'full' : available.mt1 ? 'mt1' : 'mt2'
  }, [tabChoisi, periodeInitiale, available])
  const setTab = setTabChoisi

  // Les deux periodes sont la, la vue d'ensemble non. On le DIT : un onglet qui
  // disparait sans un mot est incomprehensible pour le coach. Pas de jargon,
  // pas d'URL d'infra (P1-UX-ERREUR-001).
  const vueEnsembleManquante = Boolean(mt1 && mt2 && !full)

  const source = useMemo(() => {
    if (tab === 'mt1') return mt1
    if (tab === 'mt2') return mt2
    return full || null
  }, [tab, mt1, mt2, full])

  // Couleurs sur lesquelles l'analyse a ete faite. `null` sur tout
  // l'historique, qui n'a pas de bloc `equipes` — et on n'en deduit rien.
  const equipes = useMemo(() => analyseResultToEquipes(source), [source])
  const groups = useMemo(() => analyseResultToStatGroups(source), [source])
  const groupsMt1 = useMemo(() => (mt1 ? analyseResultToStatGroups(mt1) : null), [mt1])
  const groupsMt2 = useMemo(() => (mt2 ? analyseResultToStatGroups(mt2) : null), [mt2])

  // Les zones se lisent sur les PAYLOADS BRUTS des mi-temps, pas sur `groups` :
  // le mapper les rend deja pivotees en lignes [nous, adversaire], ce qui perd
  // l'appariement par tiers dont le retournement a besoin.
  const terrains = useMemo(() => {
    if (tab === 'mt1') return [{ titre: '1re période', zonesPct: mt1?.possession?.zones_pct }]
    if (tab === 'mt2') return [{ titre: '2e période', zonesPct: mt2?.possession?.zones_pct }]
    return [
      { titre: '1re période', zonesPct: mt1?.possession?.zones_pct },
      { titre: '2e période', zonesPct: mt2?.possession?.zones_pct },
    ]
  }, [tab, mt1, mt2])

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', fontFamily: MONO, fontSize: 12, color: MUTED }}>
        Chargement de l'analyse…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 32, textAlign: 'center', fontFamily: MONO, fontSize: 12, color: MUTED }}>
        {error}
      </div>
    )
  }

  if (!groups) {
    return (
      <div style={{ padding: 32, textAlign: 'center', fontFamily: MONO, fontSize: 12, color: MUTED }}>
        Aucun résultat d'analyse disponible.
      </div>
    )
  }

  // La comparaison des deux periodes n'a de sens que sur le match complet, et
  // seulement si les deux mi-temps sont la. Sur un onglet de periode, chaque
  // valeur est deja mesuree et affichee telle quelle.
  const comparaisonDisponible = tab === 'full' && Boolean(groupsMt1 && groupsMt2)

  return (
    <div>
      <div data-print="hide" style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${RULE}`, marginBottom: 20, overflowX: 'auto' }}>
        {TABS.filter((t) => available[t.id]).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: '12px 18px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t.id ? `2px solid ${ACCENT}` : '2px solid transparent',
              fontFamily: MONO, fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase',
              color: tab === t.id ? ACCENT : MUTED,
              fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {vueEnsembleManquante && (
        <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, marginTop: -12, marginBottom: 16 }}>
          Vue d'ensemble indisponible pour ce match.
        </div>
      )}

      <Bandeau
        category={category}
        competition={competition}
        matchDate={matchDate}
        scoreUs={scoreUs}
        scoreThem={scoreThem}
      />

      <BaseDeLAnalyse equipes={equipes} />

      <FaitDuMatch groups={groups} />
      <ChiffresCles groups={groups} />

      {/* LA POSSESSION OUVRE LE DETAIL · barre epaisse, puis les terrains.
          C'est la statistique cherchee en premier ; elle passe donc avant les
          familles de compteurs, et sous une forme qui ne se confond pas avec
          elles. */}
      <BarrePossession lignes={groups.possession} />

      {comparaisonDisponible && <ComparaisonPeriodes groupsMt1={groupsMt1} groupsMt2={groupsMt2} />}

      {/* Sur le match complet, les deux periodes cote a cote. Sur un onglet de
          periode, le terrain de cette periode seule. Le match complet n'a pas
          de terrain propre : `zones_pct` n'y est pas agregeable (P0-STATS-001). */}
      <BlocTerrain periodes={terrains} />

      {/* Les deux familles non agregeables ne sont rendues ICI que si la
          comparaison par periode n'a pas pu l'etre. Sans cette condition, le
          rapport afficherait « Voir par période » juste sous le bloc qui donne
          precisement ces valeurs : le bruit que cette refonte devait retirer. */}
      {!comparaisonDisponible && <Bloc titre="Possession" lignes={groups.possession} />}
      <Bloc titre="Passes" lignes={groups.passes} />
      <Bloc titre="Passes par zone" lignes={groups.passesZones} />
      <Bloc titre="Transitions" lignes={groups.transitions} />
      {/* Douze compteurs que le mapper produisait et que personne ne rendait. */}
      <BlocZonesTurnover lignes={groups.zonesTurnover} />
      {!comparaisonDisponible && <Bloc titre="Origine des attaques" lignes={groups.attaques} />}
      {/* Tirs et corners · meme sort que les zones de transition, produits et
          jamais affiches. `tirs` reste tolerant au tiret · le compte de tirs
          vient de `cartographies`, absent de tout l'historique. */}
      <Bloc titre="Tirs et corners" lignes={groups.tirs} />

      <Indicateurs source={source} />

      <Legende />
    </div>
  )
}
